import * as PropTypes from "prop-types"
import { I18n } from "react-polyglot-hooks"
import BrandScene from "scenes/BrandScene"
import BrandContextProvider from "scenes/BrandScene/BrandContext"
import { useRouter } from "next/router"
import LoadingScene from "scenes/LoadingScene"
import { getBrandData, getBrandsList } from "services/webService"
import { getReviews } from "services/blogService"
import { parseMDX } from "lib/mdx"
import partition from "lodash/partition"
import uniqBy from "lodash/uniqBy"
import common from "../../../../i18n/es/common"
import car from "../../../../i18n/es/car"
import brand from "../../../../i18n/es/brand"
import NotFound from "../../../404"
import { getFooterLinks, getSeoBrandData } from "../../../../src/services/seoService"
import SeoPages, { getStaticProps as getSeoStaticProps } from "../../seo_pages/[...path]"

export default function Brand({ item, notFound, footerLinks, seoPageProps }) {
  const router = useRouter()

  if (seoPageProps) {
    return <SeoPages {...seoPageProps} />
  }

  if (router.isFallback)
    return (
      <I18n locale="es" phrases={common}>
        <LoadingScene />
      </I18n>
    )

  if (notFound) return <NotFound />

  return (
    <I18n locale="es" phrases={{ ...common, ...car, ...brand }}>
      <BrandContextProvider item={item} footerLinks={footerLinks}>
        <BrandScene />
      </BrandContextProvider>
    </I18n>
  )
}

Brand.defaultProps = {
  item: { brand: {}, models: [], offers: [] },
  footerLinks: [],
  notFound: false
}

Brand.propTypes = {
  item: PropTypes.object,
  footerLinks: PropTypes.array,
  notFound: PropTypes.bool
}

export async function getStaticPaths() {
  const res = await getBrandsList("es")
  const data = await res.json()

  const paths = process.env.SKIP_PRELOAD ? [] : data.map(item => ({ params: { brand: item.slug } }))
  return { paths, fallback: "blocking" }
}

export async function getStaticProps({ params }) {
  try {
    const res = await getBrandData(params.brand, "es")
    const resSeo = await getSeoBrandData(params.brand)

    if (res.status === 404) {
      try {
        const seoProps = await getSeoStaticProps({ params: { path: ["renting", params.brand] } })
        if (!seoProps?.notFound && seoProps?.props) {
          return {
            props: {
              seoPageProps: seoProps.props
            },
            revalidate: 30
          }
        }
      } catch (seoErr) {
        console.error("Error fallback SEO page in brand route:", seoErr)
      }
      return { props: { notFound: true }, revalidate: 60 }
    }

    // Footer Links
    const footerLinksRes = await getFooterLinks()
    const footerLinks = await footerLinksRes.json()

    const item = await res.json()
    const seoData = await resSeo.json()
    const parsedMdx = await parseMDX(item.brand.description)
    const reviews = await getReviews(
      "es",
      item.models
        .filter(x => x.reviewUrl)
        .map(x => {
          const splitUrl = x.reviewUrl.split("/")
          return splitUrl[splitUrl.length - 1]
        })
    )

    const [newCars, rest] = partition(item.cars, c => c.sellingStatus === "new")
    const preOwned = uniqBy(rest.filter(c => c.sellingStatus === "pre-owned"), "model")

    return {
      props: { item: { ...item, cars: newCars, preOwned, offers: item.offers, brand: { ...item.brand, description: parsedMdx, seoData }, sellingStatus: "new", reviews }, footerLinks },
      revalidate: 30
    }
  } catch (error) {
    console.error('Error en getStaticProps de brand:', error);
    return { 
      props: { notFound: true }, 
      revalidate: 60
    };
  }
}
